import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Location, MediaAsset, Screen } from '@ghostscreen/contracts';
import { useTheme } from '../../theme/ThemeProvider';
import { WALL_ARRANGEMENTS, WALL_MODE_OPTIONS } from './wallFormat';

export type NewWallDraft = {
  name: string;
  rows: number;
  cols: number;
  mode: 'extend' | 'mirror';
  screenIds: string[];
  mediaAssetId: string;
  bezelHorizontalMm: number;
};

type NewWallDrawerProps = {
  open: boolean;
  busy: boolean;
  error: string | null;
  screens: Screen[];
  locations: Location[];
  mediaAssets: MediaAsset[];
  occupiedScreenIds: Set<string>;
  onClose: () => void;
  onCreate: (draft: NewWallDraft) => void;
};

/**
 * New wall drawer — Ghost Screen Video Walls.dc.html.
 * Create requires exact screen count + content asset; name may be empty → Untitled wall.
 */
export function NewWallDrawer({
  open,
  busy,
  error,
  screens,
  locations,
  mediaAssets,
  occupiedScreenIds,
  onClose,
  onCreate,
}: NewWallDrawerProps) {
  const { t } = useTranslation(['videoWalls', 'common']);
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [rows, setRows] = useState(2);
  const [cols, setCols] = useState(2);
  const [mode, setMode] = useState<'extend' | 'mirror'>('extend');
  const [screenIds, setScreenIds] = useState<string[]>([]);
  const [mediaAssetId, setMediaAssetId] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setRows(2);
    setCols(2);
    setMode('extend');
    setScreenIds([]);
    setMediaAssetId('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  const locationName = useMemo(() => {
    const map = new Map(locations.map((location) => [location.id, location.name]));
    return (locationId: string) => map.get(locationId) ?? t('drawer.unknownLocation');
  }, [locations, t]);

  const available = screens.filter((screen) => !occupiedScreenIds.has(screen.id));
  const needed = rows * cols;
  const remaining = Math.max(0, needed - screenIds.length);
  const picksComplete = screenIds.length === needed;
  const canCreate = picksComplete && Boolean(mediaAssetId) && !busy;
  const arrangementLabel = `${rows}×${cols}`;
  const assetLabel =
    mediaAssets.find((asset) => asset.id === mediaAssetId)?.originalFilename ??
    t('drawer.noAsset');
  const previewName = name.trim() || t('drawer.untitled');

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex justify-end"
      style={{ background: 'rgba(4,4,6,0.58)' }}
      onClick={onClose}
    >
      <aside
        data-theme={theme}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-wall-title"
        className="flex h-full w-[min(460px,100vw)] flex-col border-s shadow-[-24px_0_60px_rgba(0,0,0,0.35)] rtl:shadow-[24px_0_60px_rgba(0,0,0,0.35)]"
        style={{
          background: 'var(--gs-panel)',
          color: 'var(--gs-ink)',
          borderColor: 'var(--gs-line)',
          animation: 'gs-slide 220ms ease both',
          fontFamily: 'inherit',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <header
          className="flex shrink-0 items-start gap-3.5 border-b px-6 py-5"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span
              id="new-wall-title"
              className="text-[18px] font-extrabold tracking-[-0.02em]"
              style={{ color: 'var(--gs-ink)' }}
            >
              {t('drawer.title')}
            </span>
            <span className="text-[13px] leading-[1.45]" style={{ color: 'var(--gs-muted)' }}>
              {t('drawer.subtitle')}
            </span>
          </span>
          <button
            type="button"
            title={t('common:actions.close')}
            onClick={onClose}
            className="grid shrink-0 place-items-center p-[5px]"
            style={{ color: 'var(--gs-muted)', background: 'none', border: 'none' }}
          >
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </header>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (!canCreate) return;
            onCreate({
              name: previewName,
              rows,
              cols,
              mode,
              screenIds,
              mediaAssetId,
              bezelHorizontalMm: 0,
            });
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-[22px] overflow-y-auto px-6 py-[22px]">
            <label className="flex flex-col gap-2">
              <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                {t('drawer.nameLabel')}
              </span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('drawer.namePlaceholder')}
                className="w-full rounded-[10px] border px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
                style={{
                  color: 'var(--gs-ink)',
                  background: 'var(--gs-field)',
                  borderColor: 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              />
            </label>

            <div className="flex flex-col gap-2.5">
              <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                {t('drawer.arrangement')}
              </span>
              <div className="flex flex-wrap gap-2">
                {WALL_ARRANGEMENTS.map((option) => {
                  const selected = option.rows === rows && option.cols === cols;
                  return (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => {
                        setRows(option.rows);
                        setCols(option.cols);
                        setScreenIds([]);
                      }}
                      className="rounded-full px-[15px] py-[9px] text-sm font-semibold"
                      style={{
                        fontFamily: 'ui-monospace, Menlo, monospace',
                        background: selected ? 'var(--gs-accent-solid)' : 'var(--gs-panel2)',
                        color: selected ? 'var(--gs-accent-ink)' : 'var(--gs-ink2)',
                        border: selected ? 'none' : '1px solid var(--gs-line)',
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                {t('drawer.mode')}
              </span>
              {WALL_MODE_OPTIONS.map((option) => {
                const selected = mode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setMode(option.value)}
                    className="flex w-full items-center gap-[11px] rounded-[10px] border px-[13px] py-[11px] text-start"
                    style={{
                      background: selected ? 'var(--gs-panel2)' : 'transparent',
                      borderColor: selected ? 'var(--gs-accent)' : 'var(--gs-line)',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span
                      className="grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border-2"
                      style={{ borderColor: selected ? 'var(--gs-accent)' : 'var(--gs-line)' }}
                    >
                      <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: selected ? 'var(--gs-accent)' : 'transparent' }}
                      />
                    </span>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[13.5px] font-bold" style={{ color: 'var(--gs-ink)' }}>
                        {t(`modes.${option.value}.label`)}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--gs-muted)' }}>
                        {t(`modes.${option.value}.description`)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline gap-2.5">
                <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                  {t('drawer.screens')}
                </span>
                <span
                  className="text-xs"
                  style={{ color: picksComplete ? 'var(--gs-ok)' : 'var(--gs-muted)' }}
                >
                  {t('drawer.screensSelected', { selected: screenIds.length, total: needed })}
                </span>
              </div>
              {available.length === 0 ? (
                <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
                  {t('drawer.noFreeScreens')}
                </p>
              ) : (
                available.map((screen) => {
                  const on = screenIds.includes(screen.id);
                  return (
                    <button
                      key={screen.id}
                      type="button"
                      onClick={() => {
                        setScreenIds((current) => {
                          if (on) return current.filter((id) => id !== screen.id);
                          if (current.length >= needed) return current;
                          return [...current, screen.id];
                        });
                      }}
                      className="flex w-full items-center gap-[11px] rounded-[10px] border px-[13px] py-2.5"
                      style={{
                        background: on ? 'var(--gs-panel2)' : 'transparent',
                        borderColor: on ? 'var(--gs-accent)' : 'var(--gs-line)',
                        fontFamily: 'inherit',
                      }}
                    >
                      <span
                        className="grid h-[17px] w-[17px] shrink-0 place-items-center rounded-[5px] border-2 text-[11px] font-bold text-white"
                        style={{
                          borderColor: on ? 'var(--gs-accent)' : 'var(--gs-line)',
                          background: on ? 'var(--gs-accent-solid)' : 'transparent',
                        }}
                      >
                        {on ? '✓' : ''}
                      </span>
                      <span
                        className="min-w-0 flex-1 text-start text-[13.5px] font-semibold"
                        style={{ color: 'var(--gs-ink)' }}
                      >
                        {screen.name}
                      </span>
                      <span className="shrink-0 text-xs" style={{ color: 'var(--gs-muted)' }}>
                        {locationName(screen.locationId)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <label className="flex flex-col gap-2">
              <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
                {t('drawer.content')}
              </span>
              <select
                value={mediaAssetId}
                onChange={(event) => setMediaAssetId(event.target.value)}
                className="w-full cursor-pointer rounded-[10px] border px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
                style={{
                  color: 'var(--gs-ink)',
                  background: 'var(--gs-field)',
                  borderColor: 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              >
                <option value="">{t('drawer.selectAsset')}</option>
                {mediaAssets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.originalFilename}
                  </option>
                ))}
              </select>
            </label>

            <div
              className="flex flex-col gap-2.5 rounded-xl border px-4 py-[15px]"
              style={{ background: 'var(--gs-panel2)', borderColor: 'var(--gs-line)' }}
            >
              <span
                className="text-xs font-bold uppercase tracking-[0.14em]"
                style={{ color: 'var(--gs-muted)' }}
              >
                {t('drawer.preview')}
              </span>
              <span
                className="grid max-w-[170px] gap-[3px]"
                dir="ltr"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
              >
                {Array.from({ length: needed }, (_, index) => (
                  <span
                    key={index}
                    className="aspect-video rounded-[3px]"
                    style={{
                      background:
                        index < screenIds.length ? 'var(--gs-accent-solid)' : 'var(--gs-line)',
                    }}
                  />
                ))}
              </span>
              <span className="text-[12.5px] leading-[1.5]" style={{ color: 'var(--gs-ink2)' }}>
                {t('drawer.previewSummary', {
                  name: previewName,
                  arrangement: arrangementLabel,
                  mode: t(`modes.${mode}.inline`),
                  asset: assetLabel,
                })}{' '}
                {picksComplete
                  ? t('drawer.readyToPrepare')
                  : t('drawer.pickMore', { count: remaining })}
              </span>
            </div>

            {error ? (
              <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }}>
                {error}
              </p>
            ) : null}
          </div>

          <footer
            className="flex shrink-0 items-center gap-2.5 border-t px-6 py-4"
            style={{ borderColor: 'var(--gs-line)' }}
          >
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border px-4 py-3 text-sm font-semibold"
              style={{
                color: 'var(--gs-ink2)',
                background: 'var(--gs-panel2)',
                borderColor: 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canCreate}
              className="flex-1 rounded-[10px] border-none px-[18px] py-[13px] text-[14.5px] font-bold disabled:cursor-not-allowed"
              style={{
                color: 'var(--gs-accent-ink)',
                background: 'var(--gs-accent-solid)',
                opacity: canCreate ? 1 : 0.45,
                fontFamily: 'inherit',
              }}
            >
              {busy ? t('drawer.creating') : t('drawer.create')}
            </button>
          </footer>
        </form>
      </aside>
    </div>,
    document.body,
  );
}
